/**
 * @module lib/storage
 * @summary Centralised localStorage / sessionStorage keys + safe accessors.
 * @description
 *   // INVARIANT: these key strings are IDENTICAL to the original tool's so an
 *   existing user's autosave, custom symbols, accounts and settings survive the
 *   migration. Do NOT rename a key without a migration step.
 *
 *   The `_v1` / `_v7` suffixes are the original schema versions; bump + migrate
 *   if a stored shape's structure ever changes.
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 3985, 4451-4464, 4551,
 *   5243, 5363 and the sessionStorage 'jcc_session' (4565).
 */

/** All persistence keys in one place. */
export const KEYS = {
  /** Autosaved document (PfdDocument JSON). */
  autosave: 'jcc_pfd_autosave_v7',
  /** User-created custom symbols: Record<type, CustomSymbolRecord>. */
  customSymbols: 'jcc_custom_symbols_v1',
  /** User-added library category names: string[]. */
  customCats: 'jcc_custom_cats_v1',
  /** Built-in category renames: Record<original, renamed>. */
  catRenames: 'jcc_cat_renames_v1',
  /** Category display order: string[]. */
  catOrder: 'jcc_cat_order_v1',
  /** Hidden categories: string[]. */
  inactiveCats: 'jcc_inactive_cats_v1',
  /** Symbols moved to another category: Record<type, category>. */
  catMoves: 'jcc_cat_moves_v1',
  /** Built-in symbols the user deleted: string[]. */
  deletedSymbols: 'jcc_deleted_symbols_v1',
  /** Per-account 3D view toggles: ViewPrefs. Namespaced by username. */
  view3d: 'jcc_view3d_v1',
  /** User accounts: User[]. */
  users: 'jcc_users_v1',
  /** Symbol-library password gate (default '1234'). */
  symbolPwd: 'jcc_symbol_pwd',
  /** Anthropic API key for the AI symbol generator (browser-only). */
  apiKey: 'jcc_asp_apikey',
} as const;

/** sessionStorage key for the logged-in user. */
export const SESSION_KEY = 'jcc_session';

/**
 * Autosave key for one account. Diagrams are namespaced per user so two people
 * sharing a PC never inherit each other's work.
 *
 * Signed-out callers get the bare `KEYS.autosave`, which is also the key every
 * pre-namespacing session wrote to — see `tryAutoRestore` for the one-time
 * adoption that moves that older document to whoever signs in next.
 */
export function autosaveKey(username: string | null | undefined): string {
  return username ? `${KEYS.autosave}::${username}` : KEYS.autosave;
}

/** Read + JSON.parse a localStorage value, with a typed fallback. */
export function lsGet<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** JSON.stringify + write a localStorage value (swallows quota errors). */
export function lsSet(key: string, val: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* quota exceeded / private mode — ignore */
  }
}

/** Remove a localStorage key. */
export function lsRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
